/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, useState } from 'react';
import { InputGroup, Button } from 'react-bootstrap';
import moment from 'moment';
import {
  PriceRuleOffsetType,
  PriceRuleSource,
} from '@upstart/harmony/lib/constants';
import { CarPriceRule } from '@upstart/harmony/lib/legos/dealerTypes';
import { CreditPricing } from '@upstart/harmony/lib/legos/creditPricingType';
import { TieredAprConfig } from 'components/PricingEditor/Tiers/types';
import { ModelRulesTableForUsedProps } from './types';
import ErrorMessage from '../ErrorMessage';
import useAprConfigModal from './useAprConfigModal';
import {
  CURRENT_YEAR_PLUS_ONE_FOR_USED_CARS,
  LAST_YEAR_FOR_USED_CARS,
  HEADER_TOOLTIPS,
} from './constants';
import { YearInput } from './YearInput';
import {
  limitMonetaryInput,
  getRuleHistoryEventUpdateInfoForUsed,
} from './utils';
import {
  renderHelpToolTip,
  renderPriceRuleSourceSelector,
  renderOffsetInput,
  stopPropagation,
} from './componentsUtils';

export const ModelRulesTableForUsed: FunctionComponent<
  ModelRulesTableForUsedProps
> = ({
  index,
  dealer,
  users,
  clientType,
  condition,
  modelPriceRule,
  updatePriceRules,
  priceRulesRevisions,
  dealerPriceRules,
  updateDeletedAPRConfigs,
  currentTieredAPR,
  setTieredAprConfig,
  deleteTieredAprConfig,
  errorMessagesForYearRangeEntry,
  setErrorMessageForYearInput,
  deleteModelYearRangeEntry,
  isNewModelYearRangeEntry,
}) => {
  const [errorMessage] = useState<string>();

  const { aprConfigModal, aprConfigButton } = useAprConfigModal({
    dealer,
    clientType,
    condition,
    modelYearRangeEntry: {
      maxYear: modelPriceRule.maxYear,
      minYear: modelPriceRule.minYear,
    },
    errorMessagesForYearRangeEntry,
    aprConfig: modelPriceRule?.aprConfig,
    currentTieredAprConfig: currentTieredAPR.tieredAprConfig,
    onSaveForUsedCondition: (
      creditPricing: CreditPricing,
      tieredAprConfig: TieredAprConfig,
    ) => {
      updatePriceRules((newPriceRules) => {
        if (newPriceRules?.carRules) {
          newPriceRules.carRules[index].aprConfig = {
            enabled: true,
          };
          newPriceRules.carRules[index].creditPricing = creditPricing;
        }
      });

      setTieredAprConfig(index, { ...tieredAprConfig });
    },
  });

  const getIDForCarPriceRule = (
    prefix: string,
    maxYear: number | undefined,
    minYear: number | undefined,
  ) => {
    if (maxYear && minYear) {
      return `${prefix}:${maxYear}_${minYear}`;
    }

    if (!maxYear && !minYear) {
      return prefix;
    }
    if (maxYear) {
      return `${prefix}:${maxYear}`;
    }

    return `${prefix}:${minYear}`;
  };

  const checkIfOverlapping = (carRules: CarPriceRule[], modelYear: number) => {
    for (let i = 0; i < carRules.length; i += 1) {
      if (i === index) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const carRule = carRules[i];
      if (
        carRule.maxYear &&
        carRule.minYear &&
        carRule.minYear <= modelYear &&
        modelYear <= carRule.maxYear
      ) {
        return true;
      }
      if (carRule.maxYear === modelYear) {
        return true;
      }
      if (carRule.minYear === modelYear) {
        return true;
      }
    }
    return false;
  };

  const { updatedAt, updatedBy } = getRuleHistoryEventUpdateInfoForUsed({
    clientType,
    condition,
    users,
    priceRules: dealerPriceRules?.data?.[clientType]?.[condition],
    priceRulesRevisions,
    isDefaultCarRule: false,
    index,
  });

  return (
    <div className="default-rule-table-container">
      <table className="rule-table default-rule-table">
        <thead>
          <tr>
            <th className="year-range">
              <div className="cell-content-center-header">
                Model Year Range{' '}
                {renderHelpToolTip(
                  `pricing-matrix-source-header-help-model-year-range-${index}`,
                  HEADER_TOOLTIPS.MODEL_YEAR_RANGE,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                Source Price{' '}
                {renderHelpToolTip(
                  `pricing-matrix-source-header-help-source-${index}`,
                  HEADER_TOOLTIPS.SOURCE,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                Offset{' '}
                {renderHelpToolTip(
                  `pricing-matrix-offset-header-help-offset-${index}`,
                  HEADER_TOOLTIPS.OFFSET,
                )}
              </div>
            </th>
            <th>
              <div className="cell-content-center-header">
                Updated{' '}
                {renderHelpToolTip(
                  `pricing-matrix-updated-header-help-updated-${index}`,
                  HEADER_TOOLTIPS.UPDATED,
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="filter-menu">
                <InputGroup className="offset-input year-range">
                  <YearInput
                    year={modelPriceRule.maxYear}
                    placeholder="Max Year"
                    hasError={errorMessagesForYearRangeEntry.maxYear !== null}
                    setYear={(value) => {
                      updatePriceRules((newPriceRules) => {
                        if (newPriceRules.carRules) {
                          newPriceRules.carRules[index].maxYear = value;
                          newPriceRules.carRules[index].id =
                            getIDForCarPriceRule(
                              'year_range',
                              newPriceRules.carRules[index].maxYear,
                              newPriceRules.carRules[index].minYear,
                            );

                          if (
                            value &&
                            (value > CURRENT_YEAR_PLUS_ONE_FOR_USED_CARS ||
                              value < LAST_YEAR_FOR_USED_CARS)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'maxYear',
                              `Max Year is out of range. The allowed value is between ${CURRENT_YEAR_PLUS_ONE_FOR_USED_CARS} and ${LAST_YEAR_FOR_USED_CARS}.`,
                            );
                          } else if (!value) {
                            setErrorMessageForYearInput(
                              index,
                              'maxYear',
                              `Max Year is a required field.`,
                            );
                          } else if (
                            value &&
                            newPriceRules.carRules[index].minYear &&
                            value <
                              (newPriceRules?.carRules?.[index]?.minYear ||
                                value - 1)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'maxYear',
                              `Max Year should be greater than Min Year.`,
                            );
                          } else if (
                            checkIfOverlapping(newPriceRules.carRules, value)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'maxYear',
                              `Max Year is overlapping with other Model Year Ranges.`,
                            );
                          } else {
                            setErrorMessageForYearInput(index, 'maxYear', null);
                          }
                        }
                      });
                    }}
                    disabled={!isNewModelYearRangeEntry}
                  />
                  <YearInput
                    year={modelPriceRule.minYear}
                    placeholder="Min Year"
                    hasError={errorMessagesForYearRangeEntry.minYear !== null}
                    setYear={(value) => {
                      updatePriceRules((newPriceRules) => {
                        if (newPriceRules.carRules) {
                          newPriceRules.carRules[index].minYear = value;
                          newPriceRules.carRules[index].id =
                            getIDForCarPriceRule(
                              'year_range',
                              newPriceRules.carRules[index].maxYear,
                              newPriceRules.carRules[index].minYear,
                            );

                          if (
                            value &&
                            (value > CURRENT_YEAR_PLUS_ONE_FOR_USED_CARS ||
                              value < LAST_YEAR_FOR_USED_CARS)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'minYear',
                              `Min Year is out of range. The allowed value is between ${CURRENT_YEAR_PLUS_ONE_FOR_USED_CARS} and ${LAST_YEAR_FOR_USED_CARS}.`,
                            );
                          } else if (!value) {
                            setErrorMessageForYearInput(
                              index,
                              'minYear',
                              `Min Year is a required field.`,
                            );
                          } else if (
                            value &&
                            newPriceRules.carRules[index].maxYear &&
                            value >
                              (newPriceRules?.carRules?.[index]?.maxYear ||
                                value + 1)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'minYear',
                              `Min Year should be less than Max Year.`,
                            );
                          } else if (
                            checkIfOverlapping(newPriceRules.carRules, value)
                          ) {
                            setErrorMessageForYearInput(
                              index,
                              'minYear',
                              `Min Year is overlapping with other Model Year Ranges.`,
                            );
                          } else {
                            setErrorMessageForYearInput(index, 'minYear', null);
                          }
                        }
                      });
                    }}
                    disabled={!isNewModelYearRangeEntry}
                  />
                </InputGroup>
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                {renderPriceRuleSourceSelector(
                  modelPriceRule?.priceSource || PriceRuleSource.FEED_PRICE,
                  (priceSource) =>
                    updatePriceRules((newPriceRules) => {
                      if (newPriceRules.carRules) {
                        newPriceRules.carRules[index].priceSource = priceSource;
                      }
                    }),
                )}
              </div>
            </td>
            <td>
              <div className="cell-content-center">
                <div onClick={stopPropagation}>
                  {renderOffsetInput(
                    modelPriceRule.offsetType || PriceRuleOffsetType.ADD,
                    modelPriceRule.offsetValue,
                    (offsetType) =>
                      updatePriceRules((newPriceRules) => {
                        if (newPriceRules.carRules) {
                          newPriceRules.carRules[index].offsetType = offsetType;
                        }
                      }),
                    (offsetValue) =>
                      updatePriceRules((newPriceRules) => {
                        if (newPriceRules.carRules) {
                          if (isNaN(offsetValue as number)) {
                            newPriceRules.carRules[index].offsetValue =
                              undefined;
                          } else {
                            newPriceRules.carRules[index].offsetValue =
                              offsetValue
                                ? limitMonetaryInput(offsetValue)
                                : offsetValue;
                          }
                        }
                      }),
                  )}
                </div>
              </div>
            </td>
            <td>
              {updatedAt ? moment(updatedAt).format('YYYY-MM-DD') : ''} <br />
              {updatedBy ?? null}
            </td>
            <td> {aprConfigButton} </td>
            <td className="cell-content-right">
              <Button
                bsStyle="danger"
                onClick={() => deleteModelYearRangeEntry(index)}
              >
                Delete
              </Button>
            </td>
          </tr>
          {errorMessage && (
            <tr>
              <td colSpan={10}>
                <ErrorMessage error={errorMessage} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {(errorMessagesForYearRangeEntry.maxYear ||
        errorMessagesForYearRangeEntry.minYear) && (
        <div className="required-warning">
          <ul className="error-detail bs-callout bs-callout-info">
            {errorMessagesForYearRangeEntry.maxYear && (
              <li className="text-danger">
                {' '}
                {errorMessagesForYearRangeEntry.maxYear}{' '}
              </li>
            )}
            {errorMessagesForYearRangeEntry.minYear && (
              <li className="text-danger">
                {' '}
                {errorMessagesForYearRangeEntry.minYear}{' '}
              </li>
            )}
          </ul>
        </div>
      )}
      {aprConfigModal}
    </div>
  );
};
